import React, { useState, useCallback } from 'react';

interface PaginationParams {
  page_num: number;
  page_size: number;
  total?: number;
}

interface UsePaginationOptions<P = any> {
  /**
   * 请求方法，返回 Promise<{ list: any[], total: number }>
   */
  fetchData: (params: P & { page_num: number; page_size: number }) => Promise<{ list: any[]; total: number }>;
  /**
   * 初始搜索参数
   */
  defaultParams?: P;
  /**
   * 默认分页参数
   */
  defaultPagination?: Partial<PaginationParams>;
}

function usePagination<P = any>({ fetchData, defaultParams, defaultPagination }: UsePaginationOptions<P>) {
  const [params, setParams] = useState<P>(defaultParams || ({} as P));
  const [pagination, setPagination] = useState<PaginationParams>({
    page_num: defaultPagination?.page_num || 1,
    page_size: defaultPagination?.page_size || 10,
    total: 0,
  });
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // 查询数据
  const query = useCallback(async (extraParams?: Partial<P>) => {
    setLoading(true);
    try {
      const queryParams = {
        ...params,
        ...extraParams,
        page_num: pagination.page_num,
        page_size: pagination.page_size,
      };
      const res = await fetchData(queryParams as P & { page_num: number; page_size: number });
      setData(res.list);
      setPagination((prev) => ({ ...prev, total: res.total }));
    } finally {
      setLoading(false);
    }
  }, [params, pagination.page_num, pagination.page_size, fetchData]);

  // 搜索
  const onSearch = useCallback((searchParams: P) => {
    setParams(searchParams);
    setPagination((prev) => ({ ...prev, page_num: 1 }));
  }, []);

  // 分页变化
  const onPageChange = useCallback((page_num: number, page_size?: number) => {
    setPagination((prev) => ({
      ...prev,
      page_num,
      page_size: page_size || prev.page_size,
    }));
  }, []);

  // 监听分页或参数变化自动请求
  // 这里只能用 useEffect，不能直接在 onSearch/onPageChange 里请求，否则 setState 后数据不准
  // eslint-disable-next-line
  React.useEffect(() => {
    query();
    // eslint-disable-next-line
  }, [params, pagination.page_num, pagination.page_size]);

  return {
    data,
    loading,
    pagination: {
      current: pagination.page_num,
      page_num: pagination.page_num,
      page_size: pagination.page_size,
      total: pagination.total,
      onChange: onPageChange,
      showTotal: (total: number) => `共 ${total} 条`,
      showQuickJumper: true,
      showSizeChanger: true,
    },
    onSearch,
    onPageChange,
    params,
    setParams,
    refresh: () => query(),
  };
}

export default usePagination;
